import { createContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { getUnreadCount } from '@/services/api/notifications.api';
import { useAuth } from '@/hooks/useAuth';

export interface NotificationContextType {
  unreadCount: number;
  refreshCount: () => Promise<void>;
  isLoading: boolean;
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    if (!user?.id) {
      setUnreadCount(0);
      return;
    }

    try {
      setIsLoading(true);
      const count = await getUnreadCount(user.id);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to refresh notification count:', error);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void refreshCount();
  }, [refreshCount]);

  const value = useMemo<NotificationContextType>(
    () => ({ unreadCount, refreshCount, isLoading }),
    [unreadCount, refreshCount, isLoading]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}
